/** @file Checks the node references of a bundle puppet: parents, sockets, attachment records. */
import type { BundleError } from '@zilo/pose3d-export/check/bundleError';
import type { ExportPuppet } from '@zilo/pose3d-export/types/bundle';

/** Every (path, node) an attachment record names: `nodeMap` values, `when.hostNode`, `when.parent`, `driver`. */
function attachmentRefs(puppet: ExportPuppet): [string, string][] {
  return puppet.attachments.flatMap((a, i) => {
    const path = `/puppet/attachments/${i}`;
    const refs: [string, string][] = Object.entries(a.nodeMap).map(([from, to]) => [`${path}/nodeMap/${from}`, to]);
    if (a.when !== undefined) refs.push([`${path}/when/hostNode`, a.when.hostNode], [`${path}/when/parent`, a.when.parent]);
    if (a.driver !== undefined) refs.push([`${path}/driver`, a.driver]);
    return refs;
  });
}

/**
 * `BUNDLE_PARENT_UNKNOWN` per node whose parent is not a node; `BUNDLE_NODE_UNKNOWN` per socket or attachment
 * field naming a missing node (attachment parts are ordinary drawables, checked with the drawables).
 */
export function checkBundleNodes(puppet: ExportPuppet): BundleError[] {
  const errors: BundleError[] = [];
  const has = (n: string) => Object.hasOwn(puppet.nodes, n);
  for (const [name, node] of Object.entries(puppet.nodes)) {
    if (node.parent !== undefined && !has(node.parent)) {
      errors.push({ code: 'BUNDLE_PARENT_UNKNOWN', path: `/puppet/nodes/${name}/parent`, message: `node ${name} has unknown parent ${node.parent}` });
    }
  }
  const refs: [string, string][] = [...Object.entries(puppet.sockets).map(([s, n]): [string, string] => [`/puppet/sockets/${s}`, n]), ...attachmentRefs(puppet)];
  for (const [path, node] of refs) {
    if (!has(node)) errors.push({ code: 'BUNDLE_NODE_UNKNOWN', path, message: `${path} names unknown node ${node}` });
  }
  return errors;
}
